import {
  z, ZodAny, ZodArray,
  type ZodArrayDef, type ZodError, type ZodSchema, ZodTuple,
  type ZodTupleDef, type ZodTypeAny, ZodUnion, type ZodUnionOptions, ZodVoid
} from 'zod'

const buildCompositeResponseSchema = <T extends [ZodTypeAny, ZodTypeAny, ...ZodTypeAny[]]>(schema?: T) => z.object({
  data: z.object({
    summary: z.object({
      total: z.number(),
      failed: z.number(),
      succeeded: z.number()
    }),
    results: z.array(
      z.discriminatedUnion('status', [
        z.object({
          status: z.literal('success'),
          operation: z.string(),
          data: schema === undefined ? z.any() : z.tuple(schema)
        }),
        z.object({
          status: z.literal('failure'),
          operation: z.string(),
          context: z.record(z.any()).optional(),
          error: z.object({
            title: z.string(),
            code: z.string().optional(),
          }).optional()
        })
      ])
    )
  })
})

export async function validateCompositeResponse(fn: Promise<Response>|Response): Promise<void>
export async function validateCompositeResponse<T extends [ZodTypeAny, ZodTypeAny, ...ZodTypeAny[]]>(fn: Promise<Response>|Response, schema: T): Promise<{ operation: string, data: z.infer<ZodTuple<T>> }[]>
export async function validateCompositeResponse<T extends [ZodTypeAny, ZodTypeAny, ...ZodTypeAny[]]>(fn: Promise<Response>|Response, schema?: T): Promise<void|{ operation: string, data: z.infer<ZodTuple<T>> }[]> {
  const { response, json } = await assertResponseIsOk(fn)
  const validateSchema = buildCompositeResponseSchema(schema)
  const parsed = await validateSchema.safeParseAsync(json)

  if (!parsed.success) {
    throw new SchemaError(response, json, parsed.error)
  }

  // If there were any errors, then we need to throw an error.
  if (parsed.data.data.summary.failed > 0) {
    throw new CompositeResponseError(response, parsed.data, json)
  }

  if (schema === undefined) {
    return
  }

  return parsed.data.data.results
    .filter(x => x.status === 'success')
    .map(x => ({ operation: x.operation, data: x.data }))
}

async function assertResponseIsOk(fn: Promise<Response>|Response): Promise<{ response: Response, json: unknown }> {
  const response = await fn
  const text = await response.text()
  let json: unknown

  // If we ever can't parse it as JSON, then something went really wrong. We always need a JSON response.
  try {
    json = JSON.parse(text)
  } catch (e: unknown) {
    throw new ResponseError(response, text, undefined,'Unable to parse response as JSON.')
  }

  if (!response.ok) {
    // Attempt to parse as an error schema and throw the appropriate error.
    const parsed = await z.object({
      errors: z.array(
        z.object({
          title: z.string(),
          code: z.string().nullable(),
          source: z.object({ pointer: z.string().nullable() })
        })
      )
    }).safeParseAsync(json)

    if (parsed.success) {
      throw new ApiError(
        response,
        json,
        parsed.data.errors.map(
          x => ({
            pointer: x.source.pointer ?? undefined,
            message: x.title,
            code: x.code ?? undefined
          })
        )
      )
    }

    // If the request has failed, and we still don't understand the response here then something
    // else has gone wrong and we need to throw an error.
    throw new ResponseError(response, undefined, json, 'Unknown API response structure encountered.')
  }

  return { response, json }
}

export const validatePaginatedResponse = <T extends ZodTypeAny>(fn: Promise<Response>|Response, schema: T) => validateResponse(
  fn,
  z.object({
    data: z.array(schema),
    meta: z.object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number()
    })
  })
)


export async function validateResponse(fn: Promise<Response>|Response): Promise<void>
export async function validateResponse<T extends ZodTypeAny>(fn: Promise<Response>|Response, schema: T): Promise<z.infer<T>>
export async function validateResponse<T extends ZodTypeAny>(fn: Promise<Response>|Response, schema?: T): Promise<z.infer<T>|void> {
  const { response, json } = await assertResponseIsOk(fn)

  // No schema defined, so don't do any parsing.
  if (schema === undefined) {
    return
  }

  const dataParseResult = await schema.safeParseAsync(json)

  if (!dataParseResult.success) {
    throw new SchemaError(response, json, dataParseResult.error)
  }

  return dataParseResult.data
}

// Backwards compatibility
export { validatePaginatedResponse as validateResponseHasPaginatedSchema }
export { validateResponse as validateResponseHasSchema }
export async function validateResponseIsOk(fn: Promise<Response>|Response): Promise<Response> {
  const response = await fn
  await validateResponse(response.clone())

  return response
}


export class ResponseError extends Error
{
  public readonly response: { status: number, url: string } & ({ json: unknown, text: undefined } | { json: undefined, text: string })

  constructor(
    response: Response,
    text?: string,
    json?: unknown,
    message = 'Response failed.',
  ) {
    super(message)
    this.name = 'ResponseError'

    if (text === undefined && json === undefined) {
      throw new Error(`One of 'text' or 'json' must be provided.`)
    }

    if (json !== undefined) {
      this.response = {
        status: response.status,
        url: response.url,
        text: undefined,
        json,
      }
    } else {
      this.response = {
        status: response.status,
        url: response.url,
        text,
        json: undefined,
      }
    }
  }
}

export class ApiError extends ResponseError
{
  constructor(
    response: Response,
    json: unknown,
    public readonly errors: { pointer?: string, message: string, code?: string }[],
    message = 'API error encountered.'
  ) {
    super(response, undefined, json, message)

    this.name = 'ApiError'
  }
}

export class SchemaError extends ResponseError
{
  constructor(
    response: Response,
    json: unknown,
    public readonly error: ZodError,
    message = 'Schema assertion failed'
  ) {
    super(response, undefined, json, message)

    this.name = 'SchemaError'
  }
}

type CompositeFailure = { status: 'failure', operation: string, context: Record<string, unknown>, reason?: string }
type CompositeSuccess = { status: 'success', operation: string, data: unknown }
export class CompositeResponseError extends ResponseError
{
  public readonly partial: boolean
  public readonly results: (CompositeSuccess|CompositeFailure)[]
  public readonly successes: Omit<CompositeSuccess, 'status'>[]
  public readonly failures: Omit<CompositeFailure, 'status'>[]

  constructor(
    response: Response,
    dataWrapper: z.infer<ReturnType<typeof buildCompositeResponseSchema>>,
    json: unknown,
    message = 'Encountered a composite response with failures'
  ) {
    super(response, undefined, json, message)

    this.results = dataWrapper.data.results.map(
      x => (
        x.status === 'success'
          ? { status: x.status, operation: x.operation.toLowerCase(), data: x.data }
          : { status: x.status, operation: x.operation.toLowerCase(), reason: x.error?.title, context: x.context ?? {} }
      )
    )

    this.successes = dataWrapper.data.results
      .filter(x => x.status === 'success')
      .map(x => ({ operation: x.operation.toLowerCase(), data: x.data }))

    this.failures = dataWrapper.data.results
      .filter(x => x.status === 'failure')
      .map(x => ({
        operation: x.operation.toLowerCase(),
        reason: x.error?.title,
        context: x.context ?? {}
      }))

    this.partial = this.successes.length > 0 && this.failures.length > 0

    this.name = 'CompositeResponseError'
  }
}
